import type { AccessStackParamList } from "../../types";
import {
  getDataPointForMarkerName,
  getDataPointsForTest,
  getTestsForDataPoint,
  type ContentBlockId,
} from "@src/packages/content";

export const TEST_ROUTE_ALIASES: Record<string, string> = {
  "semen-analysis-appt": "test-semen-analysis",
  "sperm-dna-frag": "test-semen-analysis",
  "endometrial-receptivity-testing": "test-endometrial-receptivity-testing",
  "endometrial-microbiome-testing": "test-endometrial-microbiome-testing",
  "chronic-endometritis-testing": "test-chronic-endometritis-testing",
  "endometrial-add-on-bundle": "test-endometrial-add-on-bundle",
  "preimplantation-genetic-testing": "test-preimplantation-genetic-testing",
  "comprehensive-biomarker-panel": "test-comprehensive-biomarker-panel",
  era: "test-endometrial-receptivity-testing-products-era",
  emma: "test-endometrial-microbiome-testing-products-emma",
  erbiome: "test-endometrial-microbiome-testing-products-erbiome",
  alice: "test-chronic-endometritis-testing-products-alice",
  endometrio: "test-endometrial-add-on-bundle-products-endometrio",
  "er-complete": "test-endometrial-add-on-bundle-products-er-complete",
  pgt: "test-preimplantation-genetic-testing",
  "pgt-a": "test-pgt-a",
  "pgt-m": "test-pgt-m",
  "pgt-sr": "test-pgt-sr",
  "pgt-p": "test-pgt-p",
};

export const TEST_PRODUCT_ROUTE_ALIASES: Record<
  string,
  Record<string, string>
> = {
  "endometrial-receptivity-testing": {
    era: "test-endometrial-receptivity-testing-products-era",
    erpeak: "test-endometrial-receptivity-testing-products-erpeak",
    beready: "test-endometrial-receptivity-testing-products-beready",
  },
  "endometrial-microbiome-testing": {
    emma: "test-endometrial-microbiome-testing-products-emma",
    erbiome: "test-endometrial-microbiome-testing-products-erbiome",
  },
  "chronic-endometritis-testing": {
    alice: "test-chronic-endometritis-testing-products-alice",
  },
  "endometrial-add-on-bundle": {
    endometrio: "test-endometrial-add-on-bundle-products-endometrio",
    "er-complete": "test-endometrial-add-on-bundle-products-er-complete",
  },
};

export const TEST_PRODUCT_CONTENT_ROUTES = Object.entries(
  TEST_PRODUCT_ROUTE_ALIASES,
).reduce<Record<string, { testId: string; productId: string }>>(
  (routes, [testId, products]) => {
    for (const [productId, contentId] of Object.entries(products)) {
      routes[contentId] = { testId, productId };
    }
    return routes;
  },
  {},
);

export const CANONICAL_ENDOMETRIAL_TEST_IDS = new Set([
  "test-endometrial-receptivity-testing",
  "test-endometrial-microbiome-testing",
  "test-chronic-endometritis-testing",
  "test-endometrial-add-on-bundle",
]);

const TEST_CATEGORY_CONTENT_ROUTES: Record<string, string> = {
  "test-endometrial-receptivity-testing": "endometrial-receptivity-testing",
  "test-endometrial-microbiome-testing": "endometrial-microbiome-testing",
  "test-chronic-endometritis-testing": "chronic-endometritis-testing",
  "test-endometrial-add-on-bundle": "endometrial-add-on-bundle",
  "test-preimplantation-genetic-testing": "preimplantation-genetic-testing",
  "test-pgt-a": "pgt-a",
  "test-pgt-m": "pgt-m",
  "test-pgt-sr": "pgt-sr",
  "test-pgt-p": "pgt-p",
  "test-comprehensive-biomarker-panel": "comprehensive-biomarker-panel",
};

export type TestRouteTarget =
  | {
      screen: "TestDetail";
      params: AccessStackParamList["TestDetail"];
    }
  | {
      screen: "TestProductDetail";
      params: AccessStackParamList["TestProductDetail"];
    };

export function getTestRouteId(contentId: string): string {
  return TEST_CATEGORY_CONTENT_ROUTES[contentId] ?? contentId;
}

export function getTestRouteTarget(contentId: string): TestRouteTarget {
  const productRoute = TEST_PRODUCT_CONTENT_ROUTES[contentId];
  if (productRoute) {
    return {
      screen: "TestProductDetail",
      params: productRoute,
    };
  }

  return {
    screen: "TestDetail",
    params: { testId: getTestRouteId(contentId) },
  };
}

function getPreferredTestId(dataPointId: ContentBlockId): string | null {
  const tests = getTestsForDataPoint(dataPointId);
  return (
    tests.find((test) => getDataPointsForTest(test.id).length === 1)?.id ??
    tests[0]?.id ??
    null
  );
}

/**
 * Resolve a result's biomarker relationship to the canonical factsheet test.
 * Single-marker tests are preferred so a result like AMH opens AMH rather than
 * an arbitrary panel that also happens to contain it.
 */
export function getCanonicalTestIdForDataPoint(
  dataPointId: ContentBlockId | null | undefined,
): string | null {
  return dataPointId ? getPreferredTestId(dataPointId) : null;
}

export function getCanonicalTestIdForMarkerName(
  markerName: string | null | undefined,
): string | null {
  const dataPoint = markerName
    ? getDataPointForMarkerName(markerName)
    : undefined;
  return dataPoint ? getPreferredTestId(dataPoint.id) : null;
}

export function resolveRouteTestId(
  params:
    | AccessStackParamList["TestDetail"]
    | AccessStackParamList["TestProductDetail"],
): string {
  if ("productId" in params) {
    return (
      TEST_PRODUCT_ROUTE_ALIASES[params.testId]?.[params.productId] ??
      `test-${params.testId}-products-${params.productId}`
    );
  }

  return TEST_ROUTE_ALIASES[params.testId] ?? params.testId;
}
